import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import * as chatSocket from '../../lib/chatSocket';
import {
  CHAT_EVENTS,
  type ChatRoom,
  type UnreadUpdatedPayload,
} from '../../types/chat';
import { chatKeys } from './chatKeys';
import { useChatAuthToken } from './useChatAuthToken';

interface RoomsCacheShape {
  rooms?: ChatRoom[];
}

/**
 * Surfaces in-app toasts when a `chat:unread.updated` event arrives for a room
 * the user is not actively viewing. The toast is suppressed entirely while the
 * user is on a chat surface (`/chat`, `/learn/chat`) — at that point the
 * sidebar room list and inline thread already communicate the new message.
 */
export function useChatToastNotifier(): void {
  const qc = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const token = useChatAuthToken();

  useEffect(() => {
    if (!token) return;
    const off = chatSocket.on(CHAT_EVENTS.UNREAD_UPDATED, (...args) => {
      const payload = args[0] as UnreadUpdatedPayload;
      if (!payload || payload.unread_count <= 0) return;

      const onChatSurface =
        location.pathname.startsWith('/chat') ||
        location.pathname.startsWith('/learn/chat');
      if (onChatSurface) return;

      const roomsData = qc.getQueryData<RoomsCacheShape>(chatKeys.rooms());
      const room = roomsData?.rooms?.find((r) => r.id === payload.room_id);
      const title = room?.name ?? 'New message';
      const preview = payload.latest_unread_preview ?? '';
      const target = location.pathname.startsWith('/learn') ? '/learn/chat' : '/chat';

      toast(
        (t) => (
          <button
            type="button"
            onClick={() => {
              toast.dismiss(t.id);
              navigate(target);
            }}
            className="text-left"
          >
            <p className="text-sm font-semibold text-text-primary">{title}</p>
            {preview && (
              <p className="text-xs text-text-secondary line-clamp-2 mt-0.5">{preview}</p>
            )}
          </button>
        ),
        { duration: 5000 }
      );
    });
    return off;
  }, [token, qc, location.pathname, navigate]);
}
